/**
 * What differs between the diagrams one canvas edits.
 *
 * A state machine and a function's steps are the same drawing: boxes nested in
 * the element they are declared in, joined by `first a then b` edges written
 * into that element's body. Everything positional, every edit rule and the
 * canvas itself are shared; this file is only what each kind calls things and
 * which routes it reads and writes (issue #44, phases 4 and 6).
 *
 * A function's edges become transitions with no trigger, guard or effect, so
 * `stateDiagram.ts` draws them unchanged. SysML's `start` and `done` - legal
 * ends that name no element - become PSEUDO nodes inside their function, so a
 * flow is drawn with its beginning and end rather than as edges to nowhere.
 */

import { api } from '../../api'
import type { EntityRow, Transition, TransitionEdit, ViewDetail, ViewFunction, ViewState } from '../../schema'

export type EdgeField = 'trigger' | 'guard' | 'effect'

export interface DiagramKind {
  /** For the toolbar: 'state' or 'step'. */
  noun: string
  /** The API plural a box is listed, created and deleted through. */
  plural: string
  /** A container's accessible name, a nested leaf's, and a leaf's at the top: 'Function' / 'Step' / 'Function'. */
  containerLabel: string
  leafLabel: string
  topLabel: string
  /** What an edge is called: 'transition' or 'succession'. */
  edgeNoun: string
  /** The fields an edge carries beyond its two ends. */
  edgeFields: EdgeField[]
  /** Whether a new box is a usage typed by a definition - a step is; a state is not. */
  newIsTyped: boolean
  /** The view's boxes, each carrying its edges in `transitions`. */
  elements: (detail: ViewDetail) => ViewState[]
  /** The edges one element declares, read afresh. */
  readEdges: (identifier: string) => Promise<Transition[]>
  /** Replace the edges one element declares. */
  writeEdges: (identifier: string, edits: TransitionEdit[]) => Promise<unknown>
  /** What may be added to the diagram from the model's listing. */
  addable: (rows: EntityRow[]) => EntityRow[]
}

/** A succession as the transition shape everything downstream reads. */
function asTransition(edge: ViewFunction['successions'][number]): Transition {
  return { ...edge, trigger: '', guard: '', effect: '', decided_by: '', decided_by_reference: '' }
}

const PSEUDO_ENDS = ['start', 'done'] as const

/** The client-side identifier of a function's `start` or `done` node. */
export function pseudoId(functionId: string, end: 'start' | 'done'): string {
  return `${functionId}·${end}`
}

/** An edge whose `start` or `done` end now names that function's pseudo node. */
function endsToPseudo(functionId: string, edge: Transition): Transition {
  const map = (end: string) => (end === 'start' || end === 'done' ? pseudoId(functionId, end) : end)
  return { ...edge, from: map(edge.from), to: map(edge.to) }
}

/**
 * A view's functions as boxes, with a `start` and `done` node inside each
 * function whose successions use them.
 *
 * Exported for its own test: an edge to `start` that is not rewritten to the
 * pseudo node's id resolves to nothing and is counted as "not drawn", which is a
 * perfectly plausible screen for a flow that has lost its beginning.
 */
export function functionElements(functions: ViewFunction[]): ViewState[] {
  const elements: ViewState[] = []
  for (const fn of functions) {
    const used = new Set<string>()
    const transitions = fn.successions.map((edge) => {
      for (const end of PSEUDO_ENDS) {
        if (edge.from === end || edge.to === end) used.add(end)
      }
      return endsToPseudo(fn.id, asTransition(edge))
    })
    elements.push({
      id: fn.id,
      name: fn.name,
      qualified_name: fn.qualified_name,
      owner: fn.owner,
      is_definition: fn.is_definition,
      is_initial: false,
      statement: fn.type_name ? `: ${fn.type_name}${fn.statement ? ` — ${fn.statement}` : ''}` : fn.statement,
      transitions,
      shown_by: fn.shown_by,
    })
    for (const end of PSEUDO_ENDS) {
      if (!used.has(end)) continue
      elements.push({
        id: pseudoId(fn.id, end),
        name: end,
        qualified_name: `${fn.qualified_name}::${end}`,
        owner: fn.id,
        is_definition: false,
        is_initial: false,
        statement: '',
        transitions: [],
        shown_by: [],
        pseudo: end,
      })
    }
  }
  return elements
}

/**
 * An edit's ends as the file spells them: a pseudo node's end is `start` or
 * `done` again, whatever the relative-name rule made of its qualified name.
 */
function unpseudo(edits: TransitionEdit[]): TransitionEdit[] {
  const bare = (end: string) => (end.endsWith('::start') || end === 'start' ? 'start'
    : end.endsWith('::done') || end === 'done' ? 'done' : end)
  return edits.map((edit) => ({ ...edit, source: bare(edit.source), target: bare(edit.target) }))
}

export const STATE_KIND: DiagramKind = {
  noun: 'state',
  plural: 'states',
  containerLabel: 'State machine',
  leafLabel: 'State',
  topLabel: 'State',
  edgeNoun: 'transition',
  edgeFields: ['trigger', 'guard', 'effect'],
  newIsTyped: false,
  elements: (detail) => detail.states,
  readEdges: async (identifier) => (await api.state(identifier)).transitions,
  writeEdges: (identifier, edits) => api.setTransitions(identifier, edits),
  addable: (rows) => rows,
}

export const FUNCTION_KIND: DiagramKind = {
  noun: 'step',
  plural: 'functions',
  containerLabel: 'Function',
  leafLabel: 'Step',
  topLabel: 'Function',
  edgeNoun: 'succession',
  edgeFields: [],
  newIsTyped: true,
  elements: (detail) => functionElements(detail.functions ?? []),
  // Ends mapped to the pseudo nodes exactly as `functionElements` maps them, or
  // the check that the index still names the edge drawn would compare `start`
  // against the pseudo node's id and refuse every edit to a start or done edge.
  readEdges: async (identifier) =>
    (await api.function(identifier)).successions.map((edge) => endsToPseudo(identifier, asTransition(edge))),
  writeEdges: (identifier, edits) =>
    api.setSuccessions(identifier, unpseudo(edits).map(({ source, target, identifier: id, name }) => ({
      source, target, ...(id !== undefined ? { identifier: id } : {}), ...(name !== undefined ? { name } : {}),
    }))),
  addable: (rows) => rows.filter((row) => row.is_definition !== false),
}

/** The kind a view is, by its declared type. An action flow view draws functions. */
export function kindOf(detail: ViewDetail | { type_name: string }): DiagramKind {
  return String(detail.type_name ?? '').endsWith('ActionFlowView') ? FUNCTION_KIND : STATE_KIND
}
